import Button from 'components/actions/Button';
import { useMemo, useState } from 'react';
import { useListSystemTeamsQuery } from 'services/systemAdmin/teams';

import type { SystemTeam } from '../../../API';
import SystemTableHeader from '../common/SystemTableHeader';
import type { SystemTableColumn } from '../common/tableColumn';
import { getNextSortConfig, type SortConfig, sortItems } from '../common/tableSorting';
import NoContent from '../NoContent';
import TableError from '../TableError';
import TableSkeleton from '../TableSkeleton';
import TeamSettingsRow from './TeamRow';

type SortKey = 'name' | 'numberOfUsers' | 'numberOfFleets' | 'createdAt';
type TableColumnKey = SortKey | '_';

export const columns: SystemTableColumn<TableColumnKey, SortKey, SystemTeam>[] = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'numberOfUsers', label: 'No. of users', sortable: true },
    { key: 'numberOfFleets', label: 'No. of fleets', sortable: true },
    { key: 'createdAt', label: 'Created', sortable: true, sortValue: team => new Date(team.createdAt ?? '') },
    { key: '_', label: '' },
];

const SystemTeamsTable = () => {
    const {
        data: teams,
        isLoading,
        isError,
        refetch,
    } = useListSystemTeamsQuery('ListSystemTeams', {
        refetchOnMountOrArgChange: true,
    });

    const [sortConfig, setSortConfig] = useState<SortConfig<SortKey>>({ key: 'name', dir: 'asc' });

    const sortedTeams = useMemo(() => {
        if (!teams) return [];
        return sortItems(teams, sortConfig, columns);
    }, [teams, sortConfig]);

    if (isLoading) {
        return (
            <TableSkeleton id='system-teams' tableHeaders={Object.fromEntries(columns.map(c => [c.key, c.label]))} />
        );
    }

    if (!sortedTeams.length) {
        return (
            <NoContent
                id='teams'
                data-testid='system-teams-table-no-content'
                title='No teams yet, but they will appear here'
                body='As of now, there are no teams in the Control Center.'
                button={
                    <Button data-testid='no-content-teams-button' style='primary' disabled>
                        <span className='rioglyph rioglyph-plus' />
                        Add new team
                    </Button>
                }
            />
        );
    }

    /*
  	This state should be moved between loading and empty states.
   	Due to failing tests, keep it here until refactoring the feature.
  */
    if (isError) {
        return (
            <div className='TableContentWrapper'>
                <TableError header="An error occured while fetching teams' data" onClick={() => refetch()} />
            </div>
        );
    }

    return (
        <div className='display-flex flex-column'>
            <table data-testid='system-teams-table' className='table-head-filled table-bordered table'>
                <SystemTableHeader<TableColumnKey, SortKey, SystemTeam>
                    columns={columns}
                    sortConfig={sortConfig}
                    onSort={colKey => setSortConfig(prev => getNextSortConfig(prev, colKey))}
                />
                <tbody>
                    {sortedTeams.map(_ => (
                        <TeamSettingsRow key={_.id} team={_} />
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default SystemTeamsTable;
